import "core-js/modules/es.array.push.js";
import { ref, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import useUserStore from '@/composables/useUserStore';
import useUserInfoQuery from '@/composables/useUserInfo.query.js';
import useUserSettings from '@/composables/useUserSettings.query.js';
import { useI18n } from '@/plugins/i18n';
import useAbility from '@/plugins/useAbility';
import useLocale from '@/composables/useLocale';
import useSidebar from '@/composables/useSidebar';
import { filterSidebarItemsByAbility } from '@/composables/sidebarNavigation.mjs';
import defaultPhotoSmall from '@/assets/images/faces/default-user-small.svg';
import { useRouter } from 'vue-router/composables';
export default {
  __name: 'TheSidebar',
  setup(__props) {
    const {
      t,
      availableLocales
    } = useI18n();
    const {
      locale,
      setLocale,
      validateLocale
    } = useLocale();
    const {
      isAuthenticated,
      userRole
    } = useUserStore();
    const {
      sidebarItems,
      setSidebarType,
      sidebarTypes,
      initSidebar,
      openSidebar,
      closeSidebar,
      onCloseSidebar,
      isMainSidebar,
      isCompanySidebar,
      isProfileSidebar,
      isMasterDataSidebar,
      isSidebarOpen
    } = useSidebar();
    const {
      can
    } = useAbility();
    const {
      result: currentUser
    } = useUserInfoQuery();
    const {
      result: userSettings
    } = useUserSettings();
    const router = useRouter();
    const ctx = getCurrentInstance();
    const isLocaleDropdownVisible = ref(false);
    const userLanguages = computed(() => {
      return userSettings.value?.languages;
    });
    const languagesOptions = computed(() => {
      return availableLocales.filter(item => userLanguages.value?.includes(item) ?? false).map(item => {
        return {
          value: item,
          title: t('languagesName', item)
        };
      });
    });
    watch(userLanguages, languages => {
      if (languages) void validateLocale(languages);
    }, {
      immediate: true
    });
    const availableSidebarItems = computed(() => {
      return filterSidebarItemsByAbility(sidebarItems.value ?? [], can);
    });
    const groupedSidebarItems = computed(() => {
      return availableSidebarItems.value.reduce((groups, item) => {
        const group = groups[item.group] || [];
        group.push(item);
        groups[item.group] = group;
        return groups;
      }, {});
    });
    const userName = computed(() => {
      return currentUser.value?.fullName;
    });
    const userRoleValue = computed(() => {
      if (!userRole.value) {
        return '';
      }
      return t(`roles.${userRole.value}`);
    });
    const userPhoto = computed(() => {
      return currentUser.value?.photo;
    });
    const collapseAll = () => {
      var exp_element = document.getElementsByClassName('show');
      if (exp_element.length > 0) {
        var elm_id = exp_element[0].id;
        ctx.proxy.$root.$emit('bv::toggle::collapse', elm_id);
      }
    };
    const goBack = () => {
      router.push({
        name: 'WorkCenters'
      });
    };
    const goToCompanySettings = () => {
      setSidebarType(sidebarTypes.COMPANY);
      router.push({
        name: 'GeneralSettingsCompany'
      });
    };
    const goToMasterData = () => {
      setSidebarType(sidebarTypes.MASTER_DATA);
      router.push({
        name: 'LossReasons'
      });
    };
    onCloseSidebar(() => {
      cancelSidebarClose();
      isLocaleDropdownVisible.value = false;
      ctx.proxy.$refs.localeDropdown?.hide();
      ctx.proxy.$refs.profileDropdown?.hide();
    });
    let sidebarCloseTimer = null;
    function cancelSidebarClose() {
      clearTimeout(sidebarCloseTimer);
      sidebarCloseTimer = null;
    }
    function keepSidebarOpen() {
      cancelSidebarClose();
      openSidebar();
    }
    function scheduleSidebarClose() {
      cancelSidebarClose();
      if (!ctx.proxy.$refs.profileDropdown?.$el.classList.contains('show')) {
        closeSidebar();
        return;
      }
      sidebarCloseTimer = setTimeout(() => {
        sidebarCloseTimer = null;
        closeSidebar();
      }, 1000);
    }
    function handleSidebarFocusOut(event) {
      if (!ctx.proxy.$refs.sidebar?.contains(event.relatedTarget)) {
        scheduleSidebarClose();
      }
    }
    onMounted(() => {
      ctx.proxy.$refs.sidebar?.addEventListener('mouseenter', keepSidebarOpen);
      ctx.proxy.$refs.sidebar?.addEventListener('mouseleave', scheduleSidebarClose);
      ctx.proxy.$refs.sidebar?.addEventListener('focusin', keepSidebarOpen);
      ctx.proxy.$refs.sidebar?.addEventListener('focusout', handleSidebarFocusOut);
      document.querySelector('#main-container').addEventListener('click', function () {
        let sidebar = document.querySelector('#sidebar');
        if (sidebar.classList.contains('active')) {
          sidebar.classList.remove('active');
        }
      });
      initSidebar();
      ctx.proxy.$root.$on('bv::dropdown::show', bvEvent => {
        if (bvEvent.componentId === 'localeDropdown') {
          isLocaleDropdownVisible.value = true;
        }
      });
      ctx.proxy.$root.$on('bv::dropdown::hide', bvEvent => {
        if (bvEvent.componentId === 'localeDropdown') {
          isLocaleDropdownVisible.value = false;
        }
        if (isLocaleDropdownVisible.value) {
          bvEvent.preventDefault();
        }
      });
    });
    onBeforeUnmount(() => {
      cancelSidebarClose();
      ctx.proxy.$refs.sidebar?.removeEventListener('mouseenter', keepSidebarOpen);
      ctx.proxy.$refs.sidebar?.removeEventListener('mouseleave', scheduleSidebarClose);
      ctx.proxy.$refs.sidebar?.removeEventListener('focusin', keepSidebarOpen);
      ctx.proxy.$refs.sidebar?.removeEventListener('focusout', handleSidebarFocusOut);
    });
    return {
      __sfc: true,
      t,
      availableLocales,
      locale,
      setLocale,
      validateLocale,
      isAuthenticated,
      userRole,
      sidebarItems,
      setSidebarType,
      sidebarTypes,
      initSidebar,
      openSidebar,
      closeSidebar,
      onCloseSidebar,
      isMainSidebar,
      isCompanySidebar,
      isProfileSidebar,
      isMasterDataSidebar,
      isSidebarOpen,
      can,
      currentUser,
      userSettings,
      router,
      ctx,
      isLocaleDropdownVisible,
      userLanguages,
      languagesOptions,
      availableSidebarItems,
      groupedSidebarItems,
      userName,
      userRoleValue,
      userPhoto,
      collapseAll,
      goBack,
      goToCompanySettings,
      goToMasterData,
      sidebarCloseTimer,
      cancelSidebarClose,
      keepSidebarOpen,
      scheduleSidebarClose,
      handleSidebarFocusOut,
      defaultPhotoSmall
    };
  }
};